import React, { useState,useEffect } from "react";
import { toast } from 'react-toastify';
import {format } from 'date-fns';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getDefaultReviewList,getSingleReviewTemplateDetail,searchAppStoreReview } from "../../../../utils/reviewApi";
import { getSingleWidgetDetail } from "../../../../staticWidgets/utils/CommonWidgetApi";
import RatingStars from "../../../../components/RatingStars";
import ReviewPopup from "./ReviewPopup";
import BadgeWidget from "./BadgeWidget";
import FloatingWidget from "./FloatingWidget";

const CommonWidget = ({selectedLayout,isTemplateId,widgetId,reviewTabData}) => {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedReview, setSelectedReview] = useState(null);
    const [visibleGridCount, setVisibleGridCount] = useState(8);
    const [visibleListLayoutCount, setVisibleListLayoutCount] = useState(5);
    const [visibleMasonryLayoutCount, setVisibleMasonryLayoutCount] = useState(8);

    // Default values for the reviewTabData
    const {
        is_website_frontend,
        layout_is_full_width,
        business_name,
        layout_custom_width,
        style_background_color,
        header_is_widget_title,
        header_title,
        header_caption,
        setting_rating_format,
        style_widget_title_color,
        style_widget_title_fonts_title_size,
        style_widget_title_caption_color,
        style_widget_title_fonts_caption_size,
        style_header_background_color,
        header_is_show_header,
        header_write_reviews_button,
        header_source_icon,
        header_rating,
        header_number_of_reviews,
        style_header_text_color,
        style_header_write_review_background_color,
        style_header_write_review_text_color,
        header_write_review_button_type,
        style_rating_stars_color,
        layout_rows_on_mobile,
        layout_rows,
        style_load_more_button_color,
        style_load_more_button_text_color
    } = reviewTabData || {};

    // Map layout name to a specific class
    const layoutClassMap = {
        carousel_layout: 'upgadeify-carousel-style',
        list_layout: 'upgadeify-list-style',
        grid_layout: 'upgadeify-grid-style',
        masonry_layout: 'upgadeify-masonry-style',
        slider_layout: 'upgadeify-slider-style',
    };
    const layoutClass = layoutClassMap[selectedLayout] || '';

    //Helper to get slidesToShow from width
    const getSlidesToShow = (width) => {
        const w = Number(width);
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        return 1;
    };
    //State use for handle responsive logic
    const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow(layout_custom_width));

    //Update slidesToShow when window resizes or width changes
    useEffect(() => {
        const handleResize = () => {
        setSlidesToShow(getSlidesToShow(layout_custom_width));
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [layout_custom_width]);

    //Carousel Slider Settings
    const carouselSliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        arrows: true,
        autoplay: false,
        responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                dots: false,
            },
        },
        ],
    };

    //Slider Settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 3,
        arrows: false,
        autoplay: false,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                },
            },
        ],
    };

    // Fetching Review data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let result;
                if (isTemplateId) {
                    result = await getSingleReviewTemplateDetail(isTemplateId);
                } else if (widgetId) {
                    result = await getSingleWidgetDetail(widgetId);
                } else if (business_name) {
                    const payload = { business_name: business_name };
                    result = await searchAppStoreReview(payload);
                } else {
                    let app_type = 'AppStore_Review';
                    result = await getDefaultReviewList(app_type);
                }
                setApiData(result); 
            } catch (error) {
                setApiData(null); 
                toast.warning(error.message);
            } finally {
                setLoading(false); 
            }
        };

        fetchData();
    }, [isTemplateId, widgetId, business_name]);

    //Handle Layout Rows and Columns
    const layoutColumns = 3;
    // Simple: detect if the screen is mobile once at render time
    const isMobile = window.innerWidth <= 768;
    const layoutRows = isMobile
        ? layout_rows_on_mobile
        : layout_rows;

    useEffect(() => {
        const newGridCount = layoutRows * layoutColumns;
        const newMasonryCount = layoutRows * layoutColumns;

        setVisibleGridCount(newGridCount);
        setVisibleListLayoutCount(layoutRows);
        setVisibleMasonryLayoutCount(newMasonryCount);
    }, [layoutRows, layoutColumns]);

    // Open popup when clicking "Read More"
    const handleReadMoreClick = (review) => {
        setSelectedReview(review);
    };

    // Close the popup
    const handleClosePopup = () => {
        setSelectedReview(null);
    };

    // Load More List Layout function
    const handleGridLoadMore = () => {
        setVisibleGridCount((prevCount) => prevCount + 8);
    };
    
    // Load List Layout More function
    const handleListLayoutLoadMore = () => {
        setVisibleListLayoutCount((prevCount) => prevCount + 5);
    };

    // Load Masonry Layout More function
    const handleMasonryLayoutLoadMore = () => {
        setVisibleMasonryLayoutCount((prevCount) => prevCount + 8);
    };

    //Handle dynamic styles based on layout
    const dynamicStyle =
        selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout'
            ? Number(layout_is_full_width) === 1
            ? {
                backgroundColor: style_background_color,
                margin: '0 auto',
            }
            : {
                backgroundColor: style_background_color,
                maxWidth: `${layout_custom_width}px`,
                margin: '0 auto',
                }
            : {};

    // Get grid layout style based on custom width
    const getGridLayoutStyle = () => {
        if (selectedLayout !== 'grid_layout') return {};

        if (layout_custom_width > 1280 && layout_custom_width < 1921) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(24%, 1fr))',
            
            };
        }

        if (layout_custom_width > 991 && layout_custom_width < 1280) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        
            };
        }

        if (layout_custom_width > 540 && layout_custom_width < 991) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(48%, 1fr))',
            
            };
        }

        if (layout_custom_width > 0 && layout_custom_width < 540) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(100%, 1fr))',
            
            };
        }

        // Default fallback
        return {
            gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        };
    };

    return (
        <div className={`upgadeify-review-template-widget upgadeify ${layoutClass} ${selectedReview ? 'carousel-popup-open' : ''} ${is_website_frontend}`} style={dynamicStyle}>
             {/* Loader and No Api Data Handling */}
            {loading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p>Loading please wait...</p>
                </div>
            ) : apiData?.status === true ? (
            <>
                {/* Show Header Section Accordgin to condition*/}
                {selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout' && (
                    <>
                    {Number(header_is_widget_title) === 1 && (
                        <>
                            <div className="upgadeify-app-store-main-title" style={{ color: style_widget_title_color,fontSize: style_widget_title_fonts_title_size }}>{header_title || ""}</div>
                            <div className="upgadeify-app-store-main-caption" style={{ color: style_widget_title_caption_color,fontSize: style_widget_title_fonts_caption_size }}>{header_caption || ""}</div>
                        </>
                    )}
                    {Number(header_is_show_header) === 1 &&
                        (Number(header_source_icon) === 1 ||
                            Number(header_rating) === 1 ||
                            Number(header_number_of_reviews) === 1 ||
                            Number(header_write_reviews_button) === 1) && (
                            <div className="upgadeify-app-store-reviews-header" style={{ backgroundColor: style_header_background_color,}}>
                        
                                <div className='upgadeify-app-store-reviews-header-left'>
                                    {Number(header_source_icon) === 1 && (
                                    <div className='upgadeify-app-store-reviews-header-title'>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 121 36" className="injected-svg"><g clip-path="url(#a-848)"><path fill="#111" d="M77.742 10.47c.734-.295 1.524-.47 2.318-.46 1.08-.052 2.186.12 3.158.61.6.311 1.144.745 1.527 1.307.445.62.673 1.386.672 2.148-.776.015-1.553.005-2.328.005-.046-.148-.067-.302-.103-.453a2 2 0 0 0-.775-1.083c-.56-.401-1.266-.551-1.946-.521-.72-.027-1.483.154-2.027.647a1.58 1.58 0 0 0-.545 1.296c-.011.457.255.883.627 1.132.6.405 1.319.571 2.012.738 1.307.31 2.682.524 3.83 1.27.648.408 1.163 1.046 1.352 1.796.329 1.25.098 2.692-.771 3.68-.63.732-1.52 1.195-2.442 1.44a8.62 8.62 0 0 1-2.596.232c-1.112-.055-2.244-.33-3.168-.973a3.806 3.806 0 0 1-1.644-2.587 6.077 6.077 0 0 0-.063-.51c.45-.04.905-.009 1.357-.018.349.006.7-.016 1.048.015.075.664.522 1.238 1.089 1.568.474.297 1.026.433 1.576.497.461.057.924.003 1.379-.077.538-.125 1.07-.36 1.452-.77.328-.367.51-.884.408-1.373-.053-.46-.365-.85-.752-1.086-.688-.43-1.496-.59-2.274-.777-1.102-.266-2.251-.468-3.235-1.063-.611-.35-1.134-.878-1.415-1.53-.424-1.023-.394-2.242.127-3.226.456-.862 1.26-1.502 2.152-1.874Zm-38.802-.237c.902-.034 1.806-.005 2.709-.015.093-.018.11.088.138.15 1.476 4.167 2.955 8.334 4.433 12.5.13.392.29.774.405 1.17-.864.01-1.728.006-2.592.003-.394-1.17-.77-2.347-1.16-3.518-.026-.085-.13-.054-.194-.063-1.552.003-3.103.001-4.655.001-.09 0-.178.005-.266.015-.387 1.189-.772 2.38-1.175 3.563-.847.002-1.694.02-2.54-.007 1.634-4.6 3.263-9.2 4.897-13.8Zm1.373 2.417c-.08.138-.128.29-.175.442-.59 1.822-1.21 3.635-1.783 5.462.39.02.78.005 1.17.01.909-.004 1.817.01 2.725-.007-.037-.184-.1-.36-.158-.538-.524-1.595-1.043-3.191-1.566-4.787-.067-.195-.115-.398-.214-.582Zm47.8-1.128c.376-.066.759-.02 1.138-.033.418.014.84-.035 1.254.037.011.787-.002 1.575.007 2.362.633.006 1.269-.015 1.902.011.004.61.002 1.221.001 1.832-.635.014-1.27 0-1.906.006.001 1.783-.003 3.565.003 5.348-.016.423.188.892.605 1.05.406.158.85.05 1.272.057.031.307.014.616.018.924-.005.3.017.6-.022.898-.714.125-1.444.06-2.162.016-.562-.114-1.187-.263-1.557-.74-.346-.39-.45-.92-.525-1.418-.04-.461-.016-.926-.023-1.39-.006-1.58.01-3.162-.008-4.743-.484-.014-.969.014-1.452-.016 0-.609-.03-1.222.016-1.83.478-.013.957-.001 1.436-.006.016-.788.008-1.577.003-2.365Zm8.692 2.463a4.714 4.714 0 0 1 1.324-.24c.696-.044 1.406.013 2.072.232.527.13.987.431 1.426.74.8.63 1.348 1.548 1.592 2.532.145.514.178 1.048.207 1.579.012 1.26-.239 2.581-1.019 3.6-.569.785-1.441 1.317-2.368 1.571-.25.078-.514.095-.767.16-.609.02-1.229.064-1.83-.062-1.097-.178-2.142-.765-2.807-1.665-.67-.878-.983-1.99-1.014-3.084-.065-1.21.133-2.475.788-3.514a4.72 4.72 0 0 1 .936-1.073c.445-.326.922-.633 1.46-.776Zm-.2 2.65a3.294 3.294 0 0 0-.492 1.31 6.162 6.162 0 0 0-.003 2.078c.14.703.439 1.429 1.037 1.863.281.22.627.352.983.387.605.074 1.273 0 1.763-.396.325-.226.551-.565.726-.915.292-.613.383-1.303.382-1.976.01-.916-.158-1.9-.773-2.616a2.098 2.098 0 0 0-1.737-.735 2.08 2.08 0 0 0-1.885 1Zm16.688-2.109c.907-.617 2.035-.865 3.121-.781 1.177.033 2.351.537 3.125 1.436.327.385.6.82.784 1.29.23.55.329 1.143.39 1.733.012.442.022.884.012 1.326-2.363.012-4.726-.005-7.088.009-.051.944.329 2 1.202 2.463.407.252.892.305 1.357.352.649.005 1.321-.196 1.792-.659.202-.186.319-.437.422-.687h2.216c-.107.855-.598 1.632-1.272 2.156a4.647 4.647 0 0 1-1.932.909c-.501.111-1.016.12-1.527.105-1.102-.006-2.225-.363-3.052-1.107-.816-.708-1.299-1.736-1.477-2.789-.183-1.218-.146-2.501.305-3.66.323-.836.882-1.586 1.622-2.096Zm1.746 1.283c-.832.373-1.347 1.265-1.385 2.16 1.58.007 3.16.01 4.74-.001-.022-.63-.237-1.268-.678-1.73-.678-.696-1.811-.833-2.677-.43Zm-52.654-.866c.775-.89 2.021-1.246 3.171-1.147.62.067 1.236.249 1.764.585.762.506 1.323 1.281 1.623 2.14.134.326.194.673.283 1.011.219 1.44.16 2.97-.46 4.309-.402.866-1.09 1.623-1.98 1.995-.54.213-1.12.322-1.7.302-.985.031-1.996-.36-2.65-1.109-.19-.2-.316-.448-.466-.678a.785.785 0 0 0-.03.19c.007 1.362 0 2.724.003 4.086-.003.252.01.505-.02.756-.78.014-1.562.005-2.343.005a2.146 2.146 0 0 1-.03-.349c.003-.723.001-1.445.002-2.168v-2.375c.023-1.084-.027-2.168.016-3.252-.034-.981-.008-1.963-.017-2.944.004-.553-.003-1.105.003-1.658.028-.25-.03-.501.027-.748.76-.002 1.521-.01 2.282.004.027.563-.01 1.128.02 1.691.216-.176.302-.456.502-.646Zm1.65.828c-.358.072-.713.206-1.002.436-.477.363-.764.92-.938 1.483a4.753 4.753 0 0 0 .122 2.921c.226.565.626 1.086 1.189 1.343.525.229 1.135.332 1.687.138.71-.207 1.245-.815 1.514-1.486.328-.847.37-1.79.19-2.675-.147-.668-.471-1.331-1.031-1.745a2.351 2.351 0 0 0-1.73-.415Zm45.742-1.991c.293-.017.585.028.874.077.006.715 0 1.432.003 2.148-.511-.126-1.051-.17-1.567-.054-.346.091-.694.238-.94.506-.422.436-.621 1.05-.616 1.65-.011 1.978.001 3.956-.006 5.933-.796.006-1.591.011-2.387-.002-.008-3.38-.007-6.762 0-10.143.759-.01 1.52 0 2.28-.004-.002.513 0 1.026-.001 1.54l.054.244c.113-.357.257-.712.496-1.006.429-.548 1.12-.865 1.81-.889Zm-58.896 1.167c.745-.859 1.935-1.228 3.048-1.157.9.056 1.807.394 2.45 1.043.867.84 1.292 2.042 1.407 3.222.079.567.056 1.143.027 1.712-.047.186-.055.378-.08.567-.15.803-.425 1.599-.915 2.26-.533.744-1.347 1.3-2.257 1.453-.44.117-.901.091-1.353.084-.87-.07-1.73-.46-2.3-1.134-.18-.193-.296-.433-.442-.65-.056.2-.017.409-.026.614-.008 1.473.016 2.947-.012 4.42-.787.015-1.576.011-2.363.002-.014-3.986-.002-7.973-.006-11.96.004-.508-.009-1.016.007-1.524a86.341 86.341 0 0 1 2.287-.003c.06.568-.009 1.142.039 1.71l.067-.078c.152-.185.248-.412.422-.581Zm1.644.825a2.446 2.446 0 0 0-.887.357c-.54.362-.864.965-1.047 1.574-.177.58-.181 1.192-.143 1.792.083.686.285 1.39.751 1.92.388.476 1 .716 1.6.773.438.043.874-.091 1.258-.295.742-.466 1.172-1.308 1.28-2.161a4.927 4.927 0 0 0-.052-1.85c-.15-.643-.467-1.276-1.001-1.681a2.343 2.343 0 0 0-1.76-.429Z"></path><path fill="url(#b-849)" fill-rule="evenodd" d="M23.831 5.088a7.404 7.404 0 0 1 3.08 3.08l.074.14C27.648 9.592 28 10.941 28 14.446v7.108c0 3.633-.378 4.95-1.088 6.277a7.404 7.404 0 0 1-3.08 3.08l-.14.074C22.408 31.648 21.059 32 17.554 32h-7.108c-3.632 0-4.95-.378-6.277-1.088a7.404 7.404 0 0 1-3.08-3.08l-.074-.14C.365 26.432.013 25.108 0 21.743v-7.297c0-3.633.378-4.95 1.088-6.277a7.404 7.404 0 0 1 3.08-3.08l.14-.074c1.26-.65 2.584-1.002 5.949-1.015h7.297c3.633 0 4.95.378 6.277 1.088Z" clip-rule="evenodd"></path><path fill="#fff" fill-rule="evenodd" d="M15.78 13.516c.319.567.639 1.133.96 1.699.727 1.285 1.457 2.568 2.18 3.854.048.09.108.175.17.257.957-.014 1.915-.011 2.873-.001.566.007 1.084.497 1.1 1.08.035.584-.447 1.127-1.018 1.163-.565.044-1.134.03-1.7.01.33.603.678 1.197 1.014 1.798.205.338.328.767.173 1.151-.195.558-.87.889-1.413.655-.38-.137-.604-.502-.79-.844-.232-.41-.463-.822-.698-1.23-.206-.369-.413-.736-.62-1.103l-1.248-2.2c-.344-.616-.694-1.228-1.041-1.843-.225-.41-.49-.804-.626-1.256-.299-.92-.18-1.978.356-2.783.104-.14.217-.273.328-.407Zm-7.879 8.236c.889-.268 1.943-.034 2.518.727-.108.181-.214.364-.319.549-.316.53-.605 1.076-.937 1.597-.295.481-.916.762-1.464.574a1.28 1.28 0 0 1-.872-1.431c.243-.726.711-1.348 1.074-2.016ZM15.155 9.11c.486-.254 1.101-.045 1.408.396.246.388.231.894 0 1.287-.604 1.093-1.234 2.171-1.843 3.262-1.004 1.756-1.99 3.522-3.002 5.274 1.132-.003 2.263-.001 3.394 0 .602-.005 1.158.384 1.432.917.185.43.252.999-.02 1.394-3.332.01-6.664 0-9.997.005-.338-.007-.701-.005-.995-.202-.403-.245-.596-.756-.513-1.219a1.24 1.24 0 0 1 1.266-.896c.939-.004 1.879.004 2.818-.004.865-1.533 1.738-3.063 2.605-4.595.373-.65.738-1.306 1.113-1.955a84.02 84.02 0 0 1-1.134-1.997c-.221-.381-.237-.876 0-1.254.294-.453.918-.668 1.406-.416.539.253.727.875 1.03 1.348.322-.459.493-1.09 1.032-1.345Z" clip-rule="evenodd"></path></g><defs><linearGradient id="b-849" x1="14" x2="14" y1="32" y2="4" gradientUnits="userSpaceOnUse"><stop stop-color="#1C71ED"></stop><stop offset="1" stop-color="#48CEFD"></stop></linearGradient><clipPath id="a-848"><path fill="#fff" d="M0 0h120.729v36H0z"></path></clipPath></defs></svg>
                                        <div className='upgadeify-app-store-reviews-header-title-text' style={{color: style_header_text_color,}}>Reviews</div>
                                    </div>
                                    )}
                                    <div className='upgadeify-app-store-reviews-header-rating'>
                                        {Number(header_rating) === 1 && (
                                        <>
                                            <div className="upgadeify-app-store-reviews-header-rating-text" style={{color: style_header_text_color,}}>{setting_rating_format === '4.9' ? Number(apiData.data.average_rating).toFixed(1) : Number(apiData.data.average_rating).toFixed(2) }</div>
                                            <div className="upgadeify-app-store-reviews-item-rating">
                                                <RatingStars average_rating={apiData.data.average_rating} newColor={style_rating_stars_color} />
                                            </div>
                                        </>
                                        )}
                                        {Number(header_number_of_reviews) === 1 && (
                                            <span className="upgadeify-app-store-reviews-header-number-of-reviews-text">({apiData.data.total_reviews})</span>
                                        )}
                                    </div>
                                </div>
                                {Number(header_write_reviews_button) === 1 && (
                                    <div className='upgadeify-app-store-reviews-header-right'>
                                        <a
                                            href={apiData?.data?.google_url?.startsWith('http') ? apiData.data.google_url : `https://${apiData.data.google_url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className='upgadeify-app-store-reviews-header-button'
                                            style={{color: style_header_write_review_text_color,backgroundColor: style_header_write_review_background_color,fontSize: header_write_review_button_type}}>Review us on App Store
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/*If Carousel Layout Type*/}
                {selectedLayout === 'carousel_layout' && (
                    <Slider {...carouselSliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                        ))}
                    </Slider>
                )}

                {/*If List Layout Type*/}
                {selectedLayout === 'list_layout' && (
                    <>
                        <div className='upgadeify-list_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleListLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {/* Load More Button  */}
                        {visibleListLayoutCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleListLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Grid Layout Type*/}
                {selectedLayout === 'grid_layout' && (
                    <>
                        <div className='upgadeify-grid-layout' style={getGridLayoutStyle()}>
                            {apiData.data.review_list.slice(0, visibleGridCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {visibleGridCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleGridLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Masonry Layout Type*/}
                {selectedLayout === 'masonry_layout' && (
                    <>
                        <div className='upgadeify-masonry_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleMasonryLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                            
                        </div>
                            {/* Load More Button  */}
                            {visibleMasonryLayoutCount < apiData.data.review_list.length && (
                                <div className="load-more-button">
                                    <button onClick={handleMasonryLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                                </div>
                            )}
                    </>
                )}

                {/*If Slider Layout Type*/}
                {selectedLayout === 'slider_layout' && (
                    <Slider {...sliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData} />
                        ))}
                    </Slider>
                )}

                {/*If Badge Layout Type*/}
                {selectedLayout === 'badge_layout' && (
                    <BadgeWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*If Floating Badge Layout Type*/}
                {selectedLayout === 'floating_badge_layout' && (
                    <FloatingWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*Common Popup */}
                {selectedReview && <ReviewPopup review={selectedReview} onClose={handleClosePopup} />}
            </>
            ) : (
                <div className="no-inner-result-found">
                    <p>No App Store reviews found please try.</p>
                </div>
            )}
        </div >
    );
};

const ReviewLoopItem = ({ review, handleReadMoreClick, reviewTabData }) => (
    <>
        <div className="upgadeify-app-store-reviews">
            <div className='upgadeify-app-store-reviews-item' style={{ padding: `${reviewTabData.layout_item_spacing}px`, backgroundColor: reviewTabData.style_review_background_color,}}>
                <div className='upgadeify-app-store-reviews-head'>
                    {(Number(reviewTabData.review_is_show_reviewer_picture) === 1 || Number(reviewTabData.review_is_show_verified_badge) === 1) && (
                        <div className="upgadeify-app-store-reviews-item-image">
                            {review.image ? (
                                <img src={review.image} alt="Review" />
                            ) : (
                                <div className="fallback-avatar">
                                    {review.name.charAt(0)}
                                </div>
                            )}
                           
                        </div>
                    )}
                    <div className='upgadeify-app-store-reviews-item-name'>
                        {Number(reviewTabData.review_is_show_reviewer_name) === 1 && (
                        Number(reviewTabData.setting_is_enable_external_links) === 1 && review.author_url ? ( 
                                <a
                                    href={review.author_url} 
                                    target={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "_blank" : "_self"}
                                    rel={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "noopener noreferrer" : undefined}
                                    style={{ color: reviewTabData.style_review_reviewer_name_color }}
                                >
                                    <span className="upgadeify-app-store-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>{review.name}</span>
                                </a>
                            ) : (
                                <span className="upgadeify-app-store-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>
                                    {review.name}
                                </span>
                            )
                        )}

                        {Number(reviewTabData.review_is_show_verified_badge) === 1 && (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" className="review-verified-badge-icon" aria-hidden="true">
                                <path fill={reviewTabData.style_review_verified_badge_color} d="M6.757.236a.35.35 0 0 1 .486 0l1.106 1.07a.35.35 0 0 0 .329.089l1.493-.375a.35.35 0 0 1 .422.244l.422 1.48a.35.35 0 0 0 .24.24l1.481.423a.35.35 0 0 1 .244.422l-.375 1.493a.35.35 0 0 0 .088.329l1.071 1.106a.35.35 0 0 1 0 .486l-1.07 1.106a.35.35 0 0 0-.089.329l.375 1.493a.35.35 0 0 1-.244.422l-1.48.422a.35.35 0 0 0-.24.24l-.423 1.481a.35.35 0 0 1-.422.244l-1.493-.375a.35.35 0 0 0-.329.088l-1.106 1.071a.35.35 0 0 1-.486 0l-1.106-1.07a.35.35 0 0 0-.329-.089l-1.493.375a.35.35 0 0 1-.422-.244l-.422-1.48a.35.35 0 0 0-.24-.24l-1.481-.423a.35.35 0 0 1-.244-.422l.375-1.493a.35.35 0 0 0-.088-.329L.236 7.243a.35.35 0 0 1 0-.486l1.07-1.106a.35.35 0 0 0 .089-.329L1.02 3.829a.35.35 0 0 1 .244-.422l1.48-.422a.35.35 0 0 0 .24-.24l.423-1.481a.35.35 0 0 1 .422-.244l1.493.375a.35.35 0 0 0 .329-.088L6.757.236Z"></path><path fill="#fff" fill-rule="evenodd" d="M9.065 4.85a.644.644 0 0 1 .899 0 .615.615 0 0 1 .053.823l-.053.059L6.48 9.15a.645.645 0 0 1-.84.052l-.06-.052-1.66-1.527a.616.616 0 0 1 0-.882.645.645 0 0 1 .84-.052l.06.052 1.21 1.086 3.034-2.978Z" clip-rule="evenodd"></path>
                            </svg>
                        )}

                        <span className="upgadeify-app-store-reviews-item-date">
                            {Number(reviewTabData.review_is_show_date) === 1 && (
                                <span style={{color: reviewTabData.style_review_date_color,}}>{review.review_date ? format(new Date(review.review_date), 'yyyy-MM-dd') : ''}</span>
                            )}
                        </span>
                    </div>
                </div>
                <div className="upgadeify-app-store-reviews-item-content">
                   {Number(reviewTabData.review_is_show_rating) === 1 && (
                        <span className="upgadeify-app-store-reviews-item-rating" style={{ fontSize: reviewTabData.style_review_rating_size }}>
                            <RatingStars average_rating={review.rating} newColor={reviewTabData.style_rating_stars_color}/>
                        </span>
                    )}
                    <div className="upgadeify-app-store-reviews-item-text" style={{color: `${reviewTabData.style_review_text_color}`,fontSize: reviewTabData.style_review_text_font_size}}>
                        {review.description.length > 50
                        ? `${review.description.slice(0, 50)}...`
                        : review.description}
                    </div>
                    {review.description.length > 50 && (
                        <div className='upgadeify-app-store-reviews-readmore' onClick={() => handleReadMoreClick(review)} style={{color: reviewTabData.style_read_more_color,fontSize: reviewTabData.style_review_text_font_size}}>
                                Read more
                        </div>
                    )}

                    {Number(reviewTabData.review_is_show_review_source) === 1 && (  
                        <div className='upgadeify-app-store-reviews-item-image-badge'>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 25" className="injected-svg"><circle cx="12" cy="12.5" r="10" fill="url(#a-855)"></circle><path fill="#fff" fill-rule="evenodd" d="m13.258 9.556.691 1.149c.523.87 1.048 1.737 1.568 2.608.035.06.078.118.123.174.688-.01 1.378-.008 2.067-.001.408.004.78.336.792.73.025.396-.322.763-.733.788-.406.03-.816.02-1.222.006.237.408.487.81.729 1.217.147.228.236.518.124.779-.14.377-.626.6-1.017.443-.274-.093-.434-.34-.568-.572-.167-.277-.333-.556-.503-.832-.148-.25-.297-.498-.446-.746l-.898-1.489c-.247-.416-.5-.83-.75-1.246-.161-.278-.351-.544-.45-.85a2.098 2.098 0 0 1 .257-1.883c.075-.095.156-.185.236-.275Zm-5.67 5.572c.64-.181 1.398-.024 1.812.492-.078.123-.154.246-.23.37-.227.36-.436.73-.674 1.082-.212.325-.66.515-1.053.388-.429-.118-.712-.555-.629-.968.175-.491.513-.912.774-1.364Zm5.22-8.553c.35-.173.793-.031 1.013.267a.801.801 0 0 1 0 .871c-.434.74-.888 1.47-1.326 2.207-.723 1.188-1.433 2.383-2.16 3.569.814-.003 1.628-.002 2.442 0 .433-.004.833.26 1.03.62.133.291.182.676-.014.943-2.397.007-4.796 0-7.194.004-.243-.005-.505-.004-.716-.137a.789.789 0 0 1-.37-.825c.114-.376.503-.636.912-.606.675-.003 1.352.003 2.028-.003.622-1.037 1.25-2.072 1.875-3.11.268-.439.53-.882.8-1.322-.278-.447-.55-.897-.816-1.35a.79.79 0 0 1 0-.85c.212-.305.66-.451 1.012-.28.388.17.524.592.741.911.232-.31.355-.737.743-.91Z" clip-rule="evenodd"></path><defs><linearGradient id="a-855" x1="12" x2="12" y1="22.5" y2="2.5" gradientUnits="userSpaceOnUse"><stop stop-color="#1C71ED"></stop><stop offset="1" stop-color="#48CEFD"></stop></linearGradient></defs></svg>
                            Posted On   
                            <> <a href={`https://apps.apple.com`} target="blank">App Store</a></> 
                        </div> 
                    )} 
                </div>
            </div>
        </div>
    </>
);

export default CommonWidget;
